"use client";

import { TOUR_IDS } from "@bcc/components/tour/tour-ids";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@bcc/components/ui/accordion";
import { Button } from "@bcc/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@bcc/components/ui/form/form";
import { Input } from "@bcc/components/ui/input/input";
import { PasswordInput } from "@bcc/components/ui/input/password-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select/select";
import { sanitizeShopifyDomain } from "@client-shared/utilities";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  ACCESS_TOKEN_MIN_LENGTH,
  CLIENT_ID_MIN_LENGTH,
  CLIENT_SECRET_MIN_LENGTH,
  SHOP_DOMAIN_MODAL_TEST_IDS,
} from "./shop-domain-modal.utils";

type ShopifyFormValues =
  | {
      authMethod: "clientCredentials";
      shop: string;
      clientId: string;
      clientSecret: string;
      selectedLocale?: string;
    }
  | {
      authMethod: "accessToken";
      shop: string;
      accessToken: string;
      selectedLocale?: string;
    };

export type ShopifyInstallPayload =
  | {
      authMethod: "clientCredentials";
      shop: string;
      clientId: string;
      clientSecret: string;
      selectedLocale?: string;
    }
  | {
      authMethod: "accessToken";
      shop: string;
      accessToken: string;
      selectedLocale?: string;
    };

interface ShopifyInstallFormProps {
  isLoading: boolean;
  onSubmit: (payload: ShopifyInstallPayload) => Promise<void>;
  onCancel: () => void;
  languageOptions?: { label: string; value: string }[];
}

export function ShopifyInstallForm({ isLoading, onSubmit, onCancel, languageOptions }: Readonly<ShopifyInstallFormProps>) {
  const { t } = useTranslation();
  const [authMethodChosen, setAuthMethodChosen] = useState(false);

  const enterDomain = t("onboarding.shopDomainModal.errors.enterDomain");
  const invalidAccessToken = t("onboarding.shopDomainModal.errors.invalidAccessToken");
  const accessTokenTooShort = t("onboarding.shopDomainModal.errors.accessTokenTooShort");
  const clientIdTooShort = t("onboarding.shopDomainModal.errors.clientIdTooShort");
  const clientSecretTooShort = t("onboarding.shopDomainModal.errors.clientSecretTooShort");
  const clientSecretLooksLikeAccessToken = t("onboarding.shopDomainModal.errors.clientSecretLooksLikeAccessToken", {
    clientCredentialsOption: t("onboarding.shopDomainModal.shopify.authMethod.options.clientCredentials"),
    accessTokenOption: t("onboarding.shopDomainModal.shopify.authMethod.options.accessToken"),
  });

  const formSchema = z.discriminatedUnion("authMethod", [
    z.object({
      authMethod: z.literal("clientCredentials"),
      shop: z.string().min(1, enterDomain),
      clientId: z.string().min(CLIENT_ID_MIN_LENGTH, clientIdTooShort),
      clientSecret: z
        .string()
        .min(CLIENT_SECRET_MIN_LENGTH, clientSecretTooShort)
        .refine((val) => !val.startsWith("shpat_"), clientSecretLooksLikeAccessToken),
      selectedLocale: z.string().optional(),
    }),
    z.object({
      authMethod: z.literal("accessToken"),
      shop: z.string().min(1, enterDomain),
      accessToken: z
        .string()
        .min(ACCESS_TOKEN_MIN_LENGTH, accessTokenTooShort)
        .refine((val) => val.startsWith("shpat_"), invalidAccessToken),
      selectedLocale: z.string().optional(),
    }),
  ]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      authMethod: "clientCredentials",
      shop: "",
      clientId: "",
      clientSecret: "",
      selectedLocale: "",
    } as ShopifyFormValues,
  });

  const authMethod = useWatch({ control: form.control, name: "authMethod" });
  const showLanguageSelector = (languageOptions?.length ?? 0) >= 1;

  const helpSteps = t("onboarding.shopDomainModal.shopify.help.clientCredentials.steps", { returnObjects: true }) as string[];

  const handleAuthMethodSelect = (val: string) => {
    if (val === "clientCredentials") {
      form.reset({
        authMethod: "clientCredentials",
        shop: "",
        clientId: "",
        clientSecret: "",
        selectedLocale: "",
      });
    } else {
      form.reset({
        authMethod: "accessToken",
        shop: "",
        accessToken: "",
        selectedLocale: "",
      });
    }
    setAuthMethodChosen(true);
  };

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    if (values.authMethod === "clientCredentials") {
      await onSubmit({
        authMethod: "clientCredentials",
        shop: values.shop,
        clientId: values.clientId,
        clientSecret: values.clientSecret,
        selectedLocale: values.selectedLocale,
      });
    } else {
      await onSubmit({
        authMethod: "accessToken",
        shop: values.shop,
        accessToken: values.accessToken,
        selectedLocale: values.selectedLocale,
      });
    }
  };

  // Picker — shown until the user chooses an auth method. Method-specific help
  // moves to the form screen (rendered inline next to the matching fields) so
  // the user can re-read setup steps while filling them in.
  if (!authMethodChosen) {
    return (
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <label id="shopify-auth-method-picker-label" className="text-sm font-medium leading-none">
            {t("onboarding.shopDomainModal.shopify.authMethod.label")}
          </label>
          <Select onValueChange={handleAuthMethodSelect}>
            <SelectTrigger
              data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.authMethodSelect}
              aria-labelledby="shopify-auth-method-picker-label"
              className="w-full"
            >
              <SelectValue placeholder={`— ${t("onboarding.shopDomainModal.shopify.authMethod.label") as string} —`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="clientCredentials">
                {t("onboarding.shopDomainModal.shopify.authMethod.options.clientCredentials")}
              </SelectItem>
              <SelectItem value="accessToken">{t("onboarding.shopDomainModal.shopify.authMethod.options.accessToken")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-end pt-2">
          <Button type="button" variant="outline" onClick={onCancel} testId={SHOP_DOMAIN_MODAL_TEST_IDS.cancelButton}>
            {t("onboarding.shopDomainModal.cancel")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 py-4">
        {/* Auth method selector */}
        <FormField
          control={form.control}
          name="authMethod"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("onboarding.shopDomainModal.shopify.authMethod.label")}</FormLabel>
              <Select
                value={field.value}
                onValueChange={(val) => {
                  field.onChange(val);
                  const locale = form.getValues("selectedLocale");
                  if (val === "clientCredentials") {
                    form.reset({
                      authMethod: "clientCredentials",
                      shop: form.getValues("shop"),
                      clientId: "",
                      clientSecret: "",
                      selectedLocale: locale,
                    });
                  } else {
                    form.reset({
                      authMethod: "accessToken",
                      shop: form.getValues("shop"),
                      accessToken: "",
                      selectedLocale: locale,
                    });
                  }
                }}
              >
                <FormControl>
                  <SelectTrigger data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.authMethodSelect} className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="clientCredentials">
                    {t("onboarding.shopDomainModal.shopify.authMethod.options.clientCredentials")}
                  </SelectItem>
                  <SelectItem value="accessToken">{t("onboarding.shopDomainModal.shopify.authMethod.options.accessToken")}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Method-specific help — collapsed by default */}
        <Accordion type="single" collapsible data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.helpPanel} className="rounded-md bg-muted px-4">
          {authMethod === "clientCredentials" ? (
            <AccordionItem value="clientCredentials" className="border-b-0">
              <AccordionTrigger className="text-sm font-medium">
                {t("onboarding.shopDomainModal.shopify.help.clientCredentials.title")}
              </AccordionTrigger>
              <AccordionContent>
                <ol className="list-decimal space-y-1 pl-5">
                  {helpSteps.map((step) => (
                    <li key={step} className="text-sm text-muted-foreground">
                      {step}
                    </li>
                  ))}
                </ol>
              </AccordionContent>
            </AccordionItem>
          ) : (
            <AccordionItem value="accessToken" className="border-b-0">
              <AccordionTrigger className="text-sm font-medium">
                {t("onboarding.shopDomainModal.shopify.help.accessToken.title")}
              </AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-muted-foreground">{t("onboarding.shopDomainModal.shopify.help.accessToken.warning")}</p>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>

        {/* Shop domain */}
        <FormField
          control={form.control}
          name="shop"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("onboarding.shopDomainModal.shopify.fields.shopDomain")}</FormLabel>
              <FormControl>
                <div
                  className="flex items-center overflow-hidden rounded-md border border-input bg-background focus-within:ring-1 focus-within:ring-ring"
                  data-tour-id={TOUR_IDS.onboarding.shopifyShopDomain}
                >
                  <Input
                    className="flex-1 rounded-none border-0 shadow-none focus-visible:ring-0"
                    placeholder="mystore"
                    disabled={isLoading}
                    data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.shopInput}
                    {...field}
                    onChange={(e) => field.onChange(sanitizeShopifyDomain(e.target.value))}
                  />
                  <span className="select-none whitespace-nowrap pr-3 text-sm text-muted-foreground">.myshopify.com</span>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Client credentials fields */}
        {authMethod === "clientCredentials" && (
          <>
            <FormField
              control={form.control}
              name="clientId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("onboarding.shopDomainModal.shopify.fields.clientId")}</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.clientIdInput} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="clientSecret"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("onboarding.shopDomainModal.shopify.fields.clientSecret")}</FormLabel>
                  <FormControl>
                    <PasswordInput
                      disabled={isLoading}
                      testId={SHOP_DOMAIN_MODAL_TEST_IDS.clientSecretInput}
                      toggleTestId={SHOP_DOMAIN_MODAL_TEST_IDS.clientSecretToggle}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.clientSecretError} />
                </FormItem>
              )}
            />
          </>
        )}

        {/* Access token field */}
        {authMethod === "accessToken" && (
          <FormField
            control={form.control}
            name="accessToken"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("onboarding.shopDomainModal.shopify.fields.accessToken")}</FormLabel>
                <FormControl>
                  <PasswordInput
                    disabled={isLoading}
                    placeholder={t("onboarding.shopDomainModal.shopify.placeholders.accessToken")}
                    testId={SHOP_DOMAIN_MODAL_TEST_IDS.accessTokenInput}
                    toggleTestId={SHOP_DOMAIN_MODAL_TEST_IDS.accessTokenToggle}
                    {...field}
                  />
                </FormControl>
                <FormMessage data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.accessTokenError} />
              </FormItem>
            )}
          />
        )}

        {/* Language selector */}
        {showLanguageSelector && (
          <FormField
            control={form.control}
            name="selectedLocale"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("onboarding.shopDomainModal.language")}</FormLabel>
                <Select value={field.value ?? ""} onValueChange={field.onChange} disabled={isLoading}>
                  <FormControl>
                    <SelectTrigger data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.languageSelect} className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {languageOptions!.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading} testId={SHOP_DOMAIN_MODAL_TEST_IDS.cancelButton}>
            {t("onboarding.shopDomainModal.cancel")}
          </Button>
          <Button type="submit" disabled={isLoading} testId={SHOP_DOMAIN_MODAL_TEST_IDS.connectButton}>
            {isLoading ? t("onboarding.shopDomainModal.connecting") : t("onboarding.shopDomainModal.connect")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
